/**
 * homepage-search.js — Fuzzy product search for the homepage.
 * Reads product data from a JSON blob embedded by Liquid, provides
 * a Google-style dropdown with keyboard navigation, and a results
 * grid on Enter/submit.
 */
(function() {
  'use strict';

  // ── Levenshtein distance ─────────────────────────────────────────
  function levenshtein(a, b) {
    if (a.length === 0) return b.length;
    if (b.length === 0) return a.length;
    var matrix = [];
    var i, j, cost;
    for (i = 0; i <= b.length; i++) matrix[i] = [i];
    for (j = 0; j <= a.length; j++) matrix[0][j] = j;
    for (i = 1; i <= b.length; i++) {
      for (j = 1; j <= a.length; j++) {
        cost = b[i - 1] === a[j - 1] ? 0 : 1;
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + cost
        );
      }
    }
    return matrix[b.length][a.length];
  }

  // ── Fuzzy match (same logic as prices page) ──────────────────────
  function fuzzyMatch(query, text) {
    var q = query.toLowerCase();
    var t = text.toLowerCase();
    if (t.indexOf(q) >= 0) return { match: true, score: 0 };
    var words = t.split(/\s+/);
    for (var i = 0; i < words.length; i++) {
      var w = words[i];
      if (!w) continue;
      if (w.indexOf(q) === 0) return { match: true, score: 1 };
      // Reverse prefix ("souvenaid 48" starts with the word "souvenaid") only for real words.
      // A one-letter token such as the "x" in "74克 x 10包" otherwise matched every query
      // beginning with it ("xyzabc" returned 7 products). Same rule as the prices page
      // (assets/dawn--prices-page-logic.js): CJK words count at two characters, Latin at three.
      var minReverseLen = /[\u3400-\u9fff]/.test(w) ? 2 : 3;
      if (w.length >= minReverseLen && q.indexOf(w) === 0) return { match: true, score: 1 };
      if (q.length >= 2 && levenshtein(q, w.slice(0, q.length + 2)) <= 2) return { match: true, score: 2 };
    }
    return { match: false, score: 999 };
  }

  // ── Search matching ──────────────────────────────────────────────
  function getMatches(query, products, limit) {
    if (!query || !query.trim()) return [];
    var q = query.trim();
    var results = [];
    for (var i = 0; i < products.length; i++) {
      var p = products[i];
      var m = fuzzyMatch(q, p.searchText);
      if (m.match) results.push({ product: p, score: m.score });
    }
    results.sort(function(a, b) { return a.score - b.score; });
    return limit ? results.slice(0, limit) : results;
  }

  // ── HTML escaping ────────────────────────────────────────────────
  function esc(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  // ── Format money (HKD) ──────────────────────────────────────────
  function formatMoney(cents) {
    if (!cents && cents !== 0) return '';
    var amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return '$' + amount;
  }

  // ── GA4 event helper ───────────────────────────────────────────
  function trackSearch(params) {
    if (typeof gtag === 'function') {
      gtag('event', 'search', params || {});
    }
  }

  function buildSearchContext(baseParams, pageContext) {
    var params = {};
    var key;

    for (key in baseParams) {
      if (baseParams.hasOwnProperty(key)) {
        params[key] = baseParams[key];
      }
    }

    params.search_bar_location = 'sitewide_catalog_search';

    if (pageContext && pageContext.pageType) {
      params.search_origin_page_type = pageContext.pageType;
    }

    if (pageContext && pageContext.resourceHandle) {
      params.search_origin_resource_handle = pageContext.resourceHandle;
    }

    return params;
  }

  function hasHiddenTag(tags) {
    if (!Array.isArray(tags)) return false;
    return tags.indexOf('hidden') !== -1;
  }

  // ── Recent searches (shared module) ───────────────────────────────
  var RS = window.RecentSearches;
  var SearchClearBehavior = window.SearchClearBehavior;
  var SHOW_POPULAR = false; // ← toggle: true = show popular products on focus
  var POPULAR_COUNT = 4;
  var getRecentSearches = RS.getRecentSearches;
  var saveRecentSearch = RS.saveRecentSearch;
  var removeRecentSearch = RS.removeRecentSearch;

  // ── Render product image HTML ────────────────────────────────────
  function imgHTML(product, size) {
    if (product.image) {
      var src = product.image.replace(/\.([a-z]+)\?/, '_' + size + '.$1?');
      return '<img src="' + esc(src) + '" alt="' + esc(product.title) + '" loading="lazy" />';
    }
    return '<span style="font-size:0.6em;color:#aaa;">' + esc(product.title.slice(0, 8)) + '</span>';
  }

  // ── Render dropdown items ────────────────────────────────────────
  function renderDropdown(matches, dropdown) {
    if (matches.length === 0) {
      dropdown.innerHTML = '<div class="hs-dd-no-results">找不到相關產品</div>';
      dropdown.classList.add('visible');
      return;
    }

    var html = '';
    for (var i = 0; i < matches.length; i++) {
      var p = matches[i].product;
      html += '<a class="hs-dd-item" href="' + esc(p.url) + '" data-index="' + i + '">' +
        '<div class="hs-dd-img">' + imgHTML(p, '80x80_crop_center') + '</div>' +
        '<div class="hs-dd-info">' +
          '<div class="hs-dd-name">' + esc(p.title) + '</div>' +
          '<div class="hs-dd-brand">' + esc(p.vendor) + '</div>' +
        '</div>' +
        (p.price ? '<div class="hs-dd-price">' + formatMoney(p.price) + '</div>' : '') +
      '</a>';
    }
    dropdown.innerHTML = html;
    dropdown.classList.add('visible');
  }

  // ── Render results grid ──────────────────────────────────────────
  function renderResults(matches, query, resultsContainer) {
    if (!resultsContainer) return;

    if (matches.length === 0) {
      resultsContainer.innerHTML =
        '<div class="hs-no-results-msg visible">找不到「' + esc(query) + '」的相關產品</div>';
      resultsContainer.classList.add('visible');
      return;
    }

    var html = '<div class="hs-results-heading">搜尋「<strong>' + esc(query) + '</strong>」找到 ' +
      matches.length + ' 件產品</div><div class="hs-results-grid">';

    for (var i = 0; i < matches.length; i++) {
      var p = matches[i].product;
      html += '<a class="hs-result-card" href="' + esc(p.url) + '">' +
        '<div class="hs-result-img">' + imgHTML(p, '240x240') + '</div>' +
        '<div class="hs-result-info">' +
          '<div class="hs-result-name">' + esc(p.title) + '</div>' +
          '<div class="hs-result-brand">' + esc(p.vendor) + '</div>' +
          (p.price ? '<div class="hs-result-price">' + formatMoney(p.price) + '</div>' : '') +
        '</div>' +
      '</a>';
    }
    html += '</div>';
    resultsContainer.innerHTML = html;
    resultsContainer.classList.add('visible');
  }

  // ── Render suggestions (recent searches + optional popular) ─────
  function renderSuggestions(products, dropdown) {
    var recent = getRecentSearches();
    var html = '';

    if (recent.length > 0) {
      html += '<div class="hs-dd-section-header">最近搜尋</div>';
      for (var i = 0; i < recent.length; i++) {
        html += '<div class="hs-dd-recent" data-query="' + esc(recent[i]) + '">' +
          '<svg class="hs-dd-recent-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>' +
          '<span class="hs-dd-recent-text">' + esc(recent[i]) + '</span>' +
          '<button class="hs-dd-recent-remove" data-query="' + esc(recent[i]) + '" aria-label="Remove">&times;</button>' +
        '</div>';
      }
    }

    if (SHOW_POPULAR) {
      var popular = products.slice(0, POPULAR_COUNT);
      if (popular.length > 0) {
        if (recent.length > 0) html += '<div class="hs-dd-divider"></div>';
        html += '<div class="hs-dd-section-header">熱門產品</div>';
        for (var j = 0; j < popular.length; j++) {
          var p = popular[j];
          html += '<a class="hs-dd-item" href="' + esc(p.url) + '">' +
            '<div class="hs-dd-img">' + imgHTML(p, '80x80_crop_center') + '</div>' +
            '<div class="hs-dd-info">' +
              '<div class="hs-dd-name">' + esc(p.title) + '</div>' +
              '<div class="hs-dd-brand">' + esc(p.vendor) + '</div>' +
            '</div>' +
            (p.price ? '<div class="hs-dd-price">' + formatMoney(p.price) + '</div>' : '') +
          '</a>';
        }
      }
    }

    if (html) {
      dropdown.innerHTML = html;
      dropdown.classList.add('visible');
    } else {
      dropdown.classList.remove('visible');
    }
  }

  // ── Init ─────────────────────────────────────────────────────────
  function init() {
    var dataEl = document.getElementById('homepage-search-data');
    var contextEl = document.getElementById('homepage-search-context');
    if (!dataEl) return;

    var products;
    var pageContext = null;
    try {
      products = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    if (contextEl) {
      try {
        pageContext = JSON.parse(contextEl.textContent);
      } catch (err) {
        pageContext = null;
      }
    }

    // Build search text for each product
    for (var i = 0; i < products.length; i++) {
      var p = products[i];
      var tagText = Array.isArray(p.tags) ? p.tags.join(' ') : p.tags;
      p.searchText = [p.title, p.vendor, p.type, tagText].filter(Boolean).join(' ');
    }

    products = products.filter(function(product) {
      return !hasHiddenTag(product.tags);
    });

    var input = document.getElementById('homepage-search-input');
    var clearBtn = document.getElementById('homepage-search-clear');
    var dropdown = document.getElementById('homepage-search-dropdown');
    var resultsContainer = document.getElementById('homepage-search-results');

    if (!input || !dropdown) return;

    var highlightIdx = -1;
    var currentMatches = [];
    var form = input.closest('form');

    // ── Form submit (catches mobile keyboard Go/Search button) ───
    if (form) {
      form.addEventListener('submit', function(e) {
        e.preventDefault();
        if (highlightIdx >= 0) {
          var items = dropdown.querySelectorAll('.hs-dd-item');
          if (items[highlightIdx]) {
            window.location.href = items[highlightIdx].getAttribute('href');
            return;
          }
        }
        showSearchResults();
      });
    }

    // ── Input handler ──────────────────────────────────────────────
    input.addEventListener('input', function() {
      var q = input.value.trim();
      clearBtn.classList.toggle('visible', q.length > 0);

      if (q.length === 0) {
        currentMatches = [];
        highlightIdx = -1;
        renderSuggestions(products, dropdown);
        return;
      }

      currentMatches = getMatches(q, products, 6);
      highlightIdx = -1;
      renderDropdown(currentMatches, dropdown);
    });

    // ── Clear ──────────────────────────────────────────────────────
    clearBtn.addEventListener('click', function() {
      SearchClearBehavior.clearAndBlur({
        input: input,
        clearButton: clearBtn,
        dropdown: dropdown,
        onClear: function() {
          currentMatches = [];
          highlightIdx = -1;
          if (resultsContainer) resultsContainer.classList.remove('visible');
        }
      });
    });

    // ── Keyboard navigation ────────────────────────────────────────
    input.addEventListener('keydown', function(e) {
      var items = dropdown.querySelectorAll('.hs-dd-item');
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        highlightIdx = Math.min(highlightIdx + 1, items.length - 1);
        updateHighlight(items);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        highlightIdx = Math.max(highlightIdx - 1, -1);
        updateHighlight(items);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (highlightIdx >= 0 && highlightIdx < items.length) {
          // Navigate to highlighted product
          window.location.href = items[highlightIdx].getAttribute('href');
        } else {
          // Show results grid
          showSearchResults();
        }
      } else if (e.key === 'Escape') {
        dropdown.classList.remove('visible');
        highlightIdx = -1;
      }
    });

    function updateHighlight(items) {
      for (var i = 0; i < items.length; i++) {
        items[i].classList.toggle('highlighted', i === highlightIdx);
      }
      if (highlightIdx >= 0 && items[highlightIdx]) {
        items[highlightIdx].scrollIntoView({ block: 'nearest' });
      }
    }

    function showSearchResults() {
      var q = input.value.trim();
      if (!q) return;
      saveRecentSearch(q);
      dropdown.classList.remove('visible');
      var allMatches = getMatches(q, products, 0);
      trackSearch(buildSearchContext({
        search_term: q,
        results_count: allMatches.length
      }, pageContext));
      renderResults(allMatches, q, resultsContainer);
      if (resultsContainer) {
        resultsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    // ── Click outside → close dropdown ─────────────────────────────
    document.addEventListener('click', function(e) {
      if (!e.target.closest('.homepage-search-wrap')) {
        dropdown.classList.remove('visible');
        highlightIdx = -1;
      }
    });

    // ── Focus → show suggestions only when input is empty ─────────
    input.addEventListener('focus', function() {
      if (!input.value.trim()) {
        renderSuggestions(products, dropdown);
      }
    });

    // ── Delegation for recent/popular clicks ───────────────────────
    dropdown.addEventListener('click', function(e) {
      var removeBtn = e.target.closest('.hs-dd-recent-remove');
      if (removeBtn) {
        e.preventDefault();
        e.stopPropagation();
        removeRecentSearch(removeBtn.getAttribute('data-query'));
        renderSuggestions(products, dropdown);
        return;
      }
      var recentItem = e.target.closest('.hs-dd-recent');
      if (recentItem) {
        var q = recentItem.getAttribute('data-query');
        input.value = q;
        clearBtn.classList.add('visible');
        showSearchResults();
        return;
      }
      var ddItem = e.target.closest('.hs-dd-item');
      if (ddItem) {
        var nameEl = ddItem.querySelector('.hs-dd-name');
        if (nameEl) {
          saveRecentSearch(nameEl.textContent);
          trackSearch(buildSearchContext({
            search_term: input.value.trim() || nameEl.textContent,
            results_count: 1
          }, pageContext));
        }
      }
    });
  }

  // Exposed for tests (test/homepage-search-match.test.js).
  window.HomepageSearch = { fuzzyMatch: fuzzyMatch, getMatches: getMatches };

  // Boot on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
